import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';
import type { UseFormReturnType } from '@mantine/form';
import { Stack } from '@mantine/core';

import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { TextInput } from '~/domains/design-system/TextInput';
import { BRANDS } from '~/domains/auth/consts';
import dt from '~/testing';

import { ConnectedRegistrationBonusBanner } from '../../RegistrationBonusBanner/ConnectedRegistrationBonusBanner';
import { RecaptchaText } from '../../RecaptchaText';
import type { SignUpFormValues } from '../types';
import { RegistrationPromoCodeBanner } from '../../RegistrationPromoCodeBanner';

import useStyles from './styles';
import { SignUpContinueButton } from './SignUpContinueButton';

interface HandleFormProps {
  form: UseFormReturnType<SignUpFormValues>;
  onClick: () => void;
  isLoading?: boolean;
  handleKeyDownHandle: (event: any) => void;
  brand?: BRANDS;
}

export function HandleForm({
  form,
  onClick,
  isLoading,
  handleKeyDownHandle,
  brand = BRANDS.SPLASH,
}: HandleFormProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('auth');
  const isHandleError = !!form.errors.handle;
  const isHandleEmpty = !form.values.handle?.trim();
  const isNameError = !!form.errors.firstName || !!form.errors.lastName;
  const isNameEmpty = !form.values.firstName?.trim() || !form.values.lastName?.trim();
  const isButtonDisabled = isHandleError || isHandleEmpty || isNameEmpty || isNameError;

  return (
    <>
      <div className={classes.headerContainer}>
        <SignUpHeader
          title={t('signUp.headers.handle.titleWithName')}
          copy={match(brand)
            .with(BRANDS.RYP, () => t('signUp.headers.handle.rypCopy'))
            .with(BRANDS.OFP, () => t('signUp.headers.handle.ofpCopy'))
            .otherwise(() => t('signUp.headers.handle.copyWithName'))}
        />
      </div>
      <Stack sx={{ gap: /* eslint-disable-line local-rules/use-theme-tokens */ 16 }}>
        <TextInput
          data-test-id={dt.components.shared.auth.signUpVersionTwo.nameForm.firstName}
          className={classes.textInput}
          label={t('signUp.firstName.label')}
          w="100%"
          onKeyDown={handleKeyDownHandle}
          autoFocus
          {...form.getInputProps('firstName')}
        />

        <TextInput
          data-test-id={dt.components.shared.auth.signUpVersionTwo.nameForm.lastName}
          className={classes.textInput}
          label={t('signUp.lastName.label')}
          w="100%"
          onKeyDown={handleKeyDownHandle}
          {...form.getInputProps('lastName')}
        />

        <TextInput
          className={classes.textInput}
          data-test-id={dt.components.shared.auth.signUpVersionTwo.handleForm.inputField}
          errorProps={{
            'data-test-id': dt.components.shared.auth.signUpVersionTwo.handleForm.errorMessage,
          }}
          label={t('signUp.handle.label')}
          w="100%"
          onKeyDown={handleKeyDownHandle}
          {...form.getInputProps('handle')}
          onChange={(e) => form.setFieldValue('handle', e.currentTarget.value.trim())}
        />
      </Stack>

      <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={12} mt={40}>
        <RegistrationPromoCodeBanner
          promoModalProps={form.values._promoModalProps}
          referralCode={form.values._referralCodeValue}
          promoCode={form.values._promoCodeValue}
        />
        <SignUpContinueButton
          onClick={onClick}
          disabled={isButtonDisabled || isLoading}
          loading={isLoading}
          data-test-id={dt.components.shared.auth.signUpVersionTwo.handleForm.continueButton}
        />
        <ConnectedRegistrationBonusBanner />
      </Stack>

      <RecaptchaText textAlign="left" />
    </>
  );
}
